import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import * as api from '../api';
import { selectStyles, selectStylesMulti } from '../selectStyles';
import { IconChevronRight, IconClose, IconInfo, IconLayers, IconNote, IconSearch, IconTrash } from '../icons';
import * as Q from './queryOps';
import SongChipInput from './SongChipInput';
import SlotMenu from './SlotMenu';
import FilterControls from './FilterControls';
import CountPill from './CountPill';
import WhatCameNext from './WhatCameNext';
import SavedSearches from './SavedSearches';
import ShareSearchButton from './ShareSearchButton';
import MenuSelect from './MenuSelect';
import { undoToast } from './toasts';
import { featuresOf, fmtCount, normText, plural, sortOptions } from './util';
import './search.css';

const COUNT_DEBOUNCE_MS = 250;

const selStyles = { ...selectStyles, menu: (base, state) => ({ ...selectStyles.menu(base, state), zIndex: 1000 }), clearIndicator: selectStylesMulti.clearIndicator };

const LINK_REASON = {
  runKeys: 'A chain of songs played into each other can hold up to ' + Q.MAX_RUN_KEYS + ' songs.',
  runs: 'Up to ' + Q.MAX_SEQ_RUNS + ' chains in one search.',
};

const MODE_HELP = {
  all: () => 'Every song you pick must be in the show.',
  min: (n, d) => 'Shows with at least ' + n + ' of your ' + plural(d, 'song') + '.',
  closest: () => 'Shows with the most of your songs come first, even when some are missing.',
};

// Song select used for "Leave out" and the sandwich pickers: matches names
// and setlist.fm variants, most played first.
const songFilter = (option, input) => {
  const t = normText(input);
  if (!t) return true;
  const d = option.data || {};
  if (d._norm == null) return true;
  return t.split(' ').every((w) => d._norm.indexOf(w) >= 0);
};
const songOptionLabel = (o, { context }) =>
  context === 'menu' ? (
    <div className="ss-opt">
      <span className="ss-opt-main">
        <span className="ss-opt-name">{o.name}</span>
      </span>
      <span className="ss-opt-plays">{plural(Number(o.plays) || 0, 'play')}</span>
    </div>
  ) : (
    o.name
  );

function Stepper({ value, min, max, onChange, label }) {
  return (
    <span className="ss-stepper" role="group" aria-label={label}>
      <button type="button" className="ss-step" onClick={() => onChange(value - 1)} disabled={value <= min} aria-label="Fewer">
        <span aria-hidden="true">{'\u2212'}</span>
      </button>
      <span className="ss-step-val mono" aria-live="polite">
        {value}
      </span>
      <button type="button" className="ss-step" onClick={() => onChange(value + 1)} disabled={value >= max} aria-label="More">
        <span aria-hidden="true">+</span>
      </button>
    </span>
  );
}

function SandwichBuilder({ q, options, set }) {
  const [outer, setOuter] = useState(null);
  const [inner, setInner] = useState(null);
  const full = q.sandwiches.length >= Q.MAX_SANDWICHES;
  const same = outer && inner && outer.key === inner.key;
  const dup = outer && inner && q.sandwiches.some((w) => w.outer.key === outer.key && w.inner.key === inner.key);
  const add = () => {
    if (!outer || !inner || same || dup || full) return;
    set({ ...q, sandwiches: q.sandwiches.concat([{ outer: { key: outer.key, name: outer.name }, inner: { key: inner.key, name: inner.name } }]) });
    setOuter(null);
    setInner(null);
  };
  return (
    <div className="ss-field">
      <div className="ss-field-head">
        <span className="ss-label">
          <IconLayers size={12} /> Sandwich
        </span>
      </div>
      <div className="ss-sandwich">
        <div className="ss-sandwich-sel">
          <Select
            classNamePrefix="ss-rs"
            aria-label="Outer song"
            options={options}
            value={outer}
            onChange={setOuter}
            filterOption={songFilter}
            formatOptionLabel={songOptionLabel}
            getOptionValue={(o) => o.key}
            styles={selStyles}
            placeholder="Outer song"
            isDisabled={full}
            maxMenuHeight={240}
          />
        </div>
        <span className="ss-around">around</span>
        <div className="ss-sandwich-sel">
          <Select
            classNamePrefix="ss-rs"
            aria-label="Inner song"
            options={options}
            value={inner}
            onChange={setInner}
            filterOption={songFilter}
            formatOptionLabel={songOptionLabel}
            getOptionValue={(o) => o.key}
            styles={selStyles}
            placeholder="Inner song"
            isDisabled={full}
            maxMenuHeight={240}
          />
        </div>
        <button type="button" className="ss-textbtn ss-add" onClick={add} disabled={!outer || !inner || same || dup || full}>
          Add
        </button>
      </div>
      <div className="ss-note">
        {full
          ? 'Up to ' + Q.MAX_SANDWICHES + ' sandwiches in one search.'
          : same
          ? 'Pick two different songs.'
          : 'The outer song is played twice in one set with the inner song between.'}
      </div>
      {q.sandwiches.length > 0 && (
        <div className="ss-chiprow">
          {q.sandwiches.map((w) => (
            <span key={w.outer.key + '~' + w.inner.key} className="ss-gchip">
              <span>
                {w.outer.name} around {w.inner.name}
              </span>
              <button
                type="button"
                className="ss-gchip-x"
                aria-label={'Remove ' + w.outer.name + ' around ' + w.inner.name}
                onClick={() => set(Q.removePart(q, { kind: 'sandwich', value: w.outer.key + '~' + w.inner.key }))}
              >
                <IconClose size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// The whole Set Search builder (contract D.4.1). Controlled: `value` is the
// draft FindQuery, every edit goes out through onChange(q), "Find shows"
// through onSubmit(q). The live count is fetched here, debounced 250 ms.
export default function SetSearchPanel({ band, meta, catalog, value, onChange, onSubmit, onOpenSong }) {
  const q = useMemo(() => Q.normalizeQuery(value), [value]);
  const f = featuresOf(meta);
  const cat = catalog || {};
  const songs = cat.songs;
  const bandKey = band && band.key;
  const off = !!meta && meta.mode === 'off';
  const empty = Q.isEmpty(q);
  // In "At least N" mode N never exceeds the songs picked (removing a chip
  // would otherwise leave a query that can never match).
  const set = (next) => {
    if (!onChange) return;
    if (next && next.mode === 'min') {
      const d = Q.queryKeys(next).length;
      if (d >= 1 && next.minN > d) next = { ...next, minN: d };
    }
    onChange(next);
  };

  const songIndex = useMemo(() => {
    const m = {};
    (Array.isArray(songs) ? songs : []).forEach((s) => {
      if (s && s.key) m[s.key] = s;
    });
    return m;
  }, [songs]);
  const pickOptions = useMemo(
    () =>
      (Array.isArray(songs) ? songs : [])
        .filter((s) => s && s.key)
        .map((s) => ({ ...s, _norm: normText([s.name].concat(s.variants || []).join(' ')) }))
        .sort((a, b) => (Number(b.plays) || 0) - (Number(a.plays) || 0) || String(a.name).localeCompare(String(b.name))),
    [songs]
  );

  // ---- live count ----
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const [countErr, setCountErr] = useState(null);
  const reqRef = useRef(0);
  const countSig = useMemo(() => (empty ? '' : api.toFindParams(q, { withSort: false }).toString()), [q, empty]);
  useEffect(() => {
    const id = ++reqRef.current;
    if (!band || !meta || off || !countSig) {
      setCount(null);
      setCounting(false);
      setCountErr(null);
      return undefined;
    }
    setCounting(true);
    const t = setTimeout(() => {
      api
        .findCount(band, q)
        .then((r) => {
          if (reqRef.current !== id) return;
          setCount(r);
          setCountErr(null);
          setCounting(false);
        })
        .catch((e) => {
          if (reqRef.current !== id) return;
          setCount(null);
          setCountErr((e && e.userMessage) || 'Could not run that search. Try again in a moment.');
          setCounting(false);
        });
    }, COUNT_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // countSig captures the query; band by key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bandKey, countSig, !!meta, off]);
  useEffect(
    () => () => {
      reqRef.current = -1;
    },
    []
  );

  // ---- what came next ----
  const [nextFor, setNextFor] = useState(null); // { index, key } picked from a chip menu
  const [nextHidden, setNextHidden] = useState(null); // seq signature the listener closed
  useEffect(() => {
    setNextFor(null);
    setNextHidden(null);
  }, [bandKey]);
  const last = q.songs.length - 1;
  const pinned = nextFor && q.songs[nextFor.index] && q.songs[nextFor.index].key === nextFor.key ? nextFor.index : null;
  const autoIdx = last >= 1 && q.links[last - 1] === true ? last : null;
  const target = pinned != null ? pinned : autoIdx;
  const run = target != null ? Q.runAround(q, target) : null;
  const nextSeq = run ? q.songs.slice(run.start, target + 1).map((s) => s.key) : null;
  const nextSig = nextSeq ? nextSeq.join('>') : null;
  const showNext = !!nextSeq && !off && nextHidden !== nextSig;
  let extendBlocked = null;
  if (target != null) {
    if (q.songs.length >= Q.MAX_SONGS) extendBlocked = 'Up to ' + Q.MAX_SONGS + ' songs in one search.';
    else {
      const probe = Q.insertSongAfter(q, target, { key: '-probe-', name: '' }, { linked: true, allowDuplicate: true });
      const runs = Q.linkedRuns(probe);
      if (runs.some((r) => r.keys.length > Q.MAX_RUN_KEYS)) extendBlocked = LINK_REASON.runKeys;
      else if (runs.length > Q.MAX_SEQ_RUNS) extendBlocked = LINK_REASON.runs;
    }
  }
  const extend = (key, name) => {
    if (target == null || extendBlocked) return;
    const song = { key, name: (songIndex[key] && songIndex[key].name) || name || key };
    let next;
    if (target < last && q.songs[target + 1].key === key) next = Q.setLinkAt(q, target, true);
    else if (target === last) next = Q.addSong(q, song, { linked: true, allowDuplicate: true });
    else next = Q.insertSongAfter(q, target, song, { linked: true, allowDuplicate: true });
    set(next);
    if (pinned != null) setNextFor({ index: target + 1, key });
  };

  // ---- song chips ----
  const onSongs = (_songs, change) => {
    if (!change) return;
    if (change.action === 'add') set(Q.addSong(q, change.song));
    else if (change.action === 'remove') set(Q.removeSongAt(q, change.index));
    else if (change.action === 'move') set(Q.moveSong(q, change.index, change.dir));
    else if (change.action === 'clear') set({ ...q, songs: [], links: [] });
  };
  const toggleLink = (i) => {
    if (q.links[i] === true) set(Q.setLinkAt(q, i, false));
    else if (!Q.linkBlocked(q, i)) set(Q.setLinkAt(q, i, true));
  };
  const linkReason = (i) => {
    const r = Q.linkBlocked(q, i);
    return r && r !== 'range' ? LINK_REASON[r] : null;
  };
  const slotsFull = Q.slotCount(q) >= Q.MAX_SLOTS;
  const jamsFull = Q.jamCount(q) >= Q.MAX_JAMS;
  const renderChipMenu = (song, index, content, { move, count: n }) => {
    const row = songIndex[song.key];
    return (
      <SlotMenu
        slot={song.slot}
        jam={song.jam}
        features={f}
        onChange={(p) => set(Q.updateSongAt(q, index, p))}
        title={song.name}
        subtitle={row && Number(row.plays) >= 0 ? 'Played ' + plural(Number(row.plays), 'time') : ''}
        slotFull={slotsFull}
        jamFull={jamsFull}
        buttonLabel={'Options for ' + song.name}
        actions={[
          {
            key: 'next',
            label: 'What came next',
            icon: <IconChevronRight size={14} />,
            onSelect: () => {
              setNextFor({ index, key: song.key });
              setNextHidden(null);
            },
          },
          onOpenSong && { key: 'song', label: 'Open song page', icon: <IconNote size={14} />, onSelect: () => onOpenSong(song.key) },
          { key: 'left', label: 'Move left', icon: <IconChevronRight size={14} className="ss-flip" />, disabled: index === 0, onSelect: () => move(-1) },
          { key: 'right', label: 'Move right', icon: <IconChevronRight size={14} />, disabled: index >= n - 1, onSelect: () => move(1) },
          { key: 'remove', label: 'Remove', icon: <IconTrash size={14} />, onSelect: () => set(Q.removeSongAt(q, index)) },
        ]}
      >
        {content}
      </SlotMenu>
    );
  };

  const clearAll = () => {
    const prev = q;
    set({ ...Q.emptyQuery(), sort: q.sort });
    undoToast('Set Search cleared.', () => set(prev));
  };

  // ---- mode, sort ----
  const distinct = Q.queryKeys(q).length;
  const minMax = Math.max(1, Math.min(Q.MAX_SONGS, distinct || 1));
  const minN = Math.min(q.minN, minMax);
  const sorts = sortOptions(f.sorts);

  if (!meta) {
    return (
      <div className="ss-panel" aria-busy="true">
        <div className="skel-row">
          <div className="skel skel-line w60" />
          <div className="skel skel-line w90" />
          <div className="skel skel-line w40" />
        </div>
      </div>
    );
  }
  if (off) {
    return (
      <div className="ss-panel">
        <div className="empty-state">
          <div className="empty-title">Set Search is not ready for {band ? band.name : 'this band'} yet.</div>
        </div>
      </div>
    );
  }

  // Coverage (SPEC 7.1): setlist bands with tape-title fills count both sources.
  const c = meta.coverage || {};
  const cov = Number(c.showsIndexed);
  const fills = c.tracksFill ? Number(c.tracksFill.shows) : 0;
  const slShows = Number(c.setlistShows);
  const modeNote =
    meta.mode === 'tracks'
      ? 'Matched from tape track titles.'
      : fills > 0 && slShows > 0
        ? 'Searches ' + fmtCount(cov > 0 ? cov : slShows + fills) + ' shows: ' + fmtCount(slShows) + ' with a known setlist, ' +
          fmtCount(fills) + ' matched from tape track titles.'
        : 'Searches ' + (cov > 0 ? fmtCount(cov) + ' ' : '') + 'shows with a known setlist.';

  return (
    <div className="ss-panel">
      <div className="ss-modenote">
        <IconInfo size={13} />
        <span>{modeNote}</span>
      </div>

      <div className="ss-field">
        <div className="ss-field-head">
          <label className="ss-label" htmlFor="ss-song-input">
            Songs
          </label>
          {!empty && (
            <button type="button" className="ss-textbtn" onClick={clearAll}>
              Clear all
            </button>
          )}
        </div>
        <SongChipInput
          options={songs || []}
          value={q.songs}
          onChange={onSongs}
          links={q.links}
          onToggleLink={toggleLink}
          linkBlocked={linkReason}
          renderChipMenu={renderChipMenu}
          isLoading={!Array.isArray(songs)}
        />
        {q.songs.length >= 2 && !q.links.some(Boolean) && (
          <div className="ss-note">Click "and" between two songs to ask for one played into the next.</div>
        )}
        {q.songs.length === 1 && !showNext && (
          <div className="ss-note">Open a song's menu for where it was played, long versions and what came next.</div>
        )}
      </div>

      {showNext && (
        <WhatCameNext
          band={band}
          seq={nextSeq}
          onExtend={extend}
          onClose={() => {
            setNextHidden(nextSig);
            setNextFor(null);
          }}
          disabledReason={extendBlocked}
        />
      )}

      <div className="ss-field">
        <div className="ss-field-head">
          <span className="ss-label" id="ss-mode-label">
            Match
          </span>
        </div>
        <div className="ss-seg" role="radiogroup" aria-labelledby="ss-mode-label">
          {['all', 'min', 'closest'].map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={q.mode === m}
              className={'ss-seg-btn' + (q.mode === m ? ' on' : '')}
              onClick={() => set({ ...q, mode: m, minN: m === 'min' ? minN : q.minN })}
            >
              {Q.MODE_LABELS[m]}
            </button>
          ))}
        </div>
        {q.mode === 'min' && (
          <div className="ss-minrow">
            <span>At least</span>
            <Stepper value={minN} min={1} max={minMax} onChange={(v) => set({ ...q, minN: Math.max(1, Math.min(minMax, v)) })} label="How many songs" />
            <span>of your {plural(distinct, 'song')}</span>
          </div>
        )}
        <div className="ss-note">{MODE_HELP[q.mode](minN, distinct)}</div>
        {q.mode !== 'all' && distinct < 2 && <div className="ss-note">Pick 2 or more songs for this to matter.</div>}
        {q.mode === 'closest' && (
          <div className="ss-score">
            <IconInfo size={13} />
            <span>
              Score: 100 x songs found / songs picked, plus up to 20 for songs played in your order. Leave-outs, places in the show and the
              filters below still have to match.
            </span>
          </div>
        )}
      </div>

      <div className="ss-field">
        <div className="ss-field-head">
          <span className="ss-label">Leave out</span>
        </div>
        <Select
          classNamePrefix="ss-rs"
          aria-label="Leave out a song"
          options={pickOptions.filter((o) => !q.not.some((n) => n.key === o.key))}
          value={null}
          onChange={(o) => o && set({ ...q, not: q.not.concat([{ key: o.key, name: o.name }]) })}
          filterOption={songFilter}
          formatOptionLabel={songOptionLabel}
          getOptionValue={(o) => o.key}
          styles={selStyles}
          placeholder={q.not.length >= Q.MAX_NOT ? 'Up to ' + Q.MAX_NOT + ' songs' : 'Shows without this song...'}
          isDisabled={q.not.length >= Q.MAX_NOT}
          maxMenuHeight={240}
        />
        {q.not.length > 0 && (
          <div className="ss-chiprow">
            {q.not.map((n) => (
              <span key={n.key} className="ss-gchip">
                <span>Not {n.name}</span>
                <button type="button" className="ss-gchip-x" aria-label={'Remove Not ' + n.name} onClick={() => set(Q.removePart(q, { kind: 'not', value: n.key }))}>
                  <IconClose size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {f.sandwich && <SandwichBuilder key={bandKey} q={q} options={pickOptions} set={set} />}

      <FilterControls meta={meta} catalog={cat} value={q} onChange={set} />

      {sorts.length > 1 && (
        <div className="ss-field ss-sortrow">
          <span className="ss-label">Sort</span>
          {q.mode === 'closest' ? (
            <span className="ss-note">Closest match first</span>
          ) : (
            <MenuSelect value={q.sort} options={sorts} onChange={(s) => set({ ...q, sort: s })} title="Sort the results" />
          )}
        </div>
      )}

      <SavedSearches band={band} current={q} onLoad={(loaded) => set(loaded)} catalog={cat} meta={meta} />

      <div className="ss-foot">
        {empty ? (
          <div className="ss-note ss-foot-hint">Add a song or a filter to start.</div>
        ) : (
          <CountPill count={count} loading={counting} onDrop={(e) => set(Q.applyDrop(q, e))} error={countErr} />
        )}
        <ShareSearchButton band={band} query={q} />
        <button type="button" className="btn-pill ss-find" onClick={() => onSubmit && onSubmit(q)} disabled={empty}>
          <IconSearch size={14} />
          <span>Find shows</span>
        </button>
      </div>
    </div>
  );
}
